import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
  Autocomplete,
  TextField,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { ShepButton } from "@/components/GenericComponents/ShepComponents";
import axios from "@/features/axiosHelper";
import { useDispatch } from "react-redux";
import { createAlert } from "@/features/homepage/alertSliceList";
import { useWorkflowLookups } from "../WorkflowLookupsContext";
import { CatalogTypes } from "@/components/Maintenance/MaintenanceTypes";
import { WorkflowLookupMode } from "@/components/Maintenance/MaintenanceTypes";

const labelSx = { mr: 3, minWidth: 280, alignItems: "right" };

const AddCustomerWarehousePopup = ({
  open,
  onClose,
  workflowLookupType,
  fetchData,
  mode = WorkflowLookupMode.Add,
  existingRow = null,
}) => {
  const dispatch = useDispatch();
  const {
    fetchCatalogArtifactData,
    fetchAssociatedConnections,
    customerConnections,
    customersLoaded,
    rows,
  } = useWorkflowLookups();
  const [selectedCustomerConnection, setSelectedCustomerConnection] =
    useState(null);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);

  const [customerWarehouses, setCustomerWarehouses] = useState([]);
  const [customerWarehousesLoading, setCustomerWarehousesLoading] =
    useState(false);

  const [associatedErps, setAssociatedErps] = useState([]);
  const [associatedErpsLoading, setAssociatedErpsLoading] = useState(false);
  const [selectedErp, setSelectedErp] = useState(null);

  // Helper for code-first label
  const codeFirstLabel = (code, name) => {
    if (!code) return name || "";
    if (name && name !== "" && name !== code) return `${code} - ${name}`;
    return code;
  };

  // Prefill on edit
  useEffect(() => {
    if (!open) return;

    if (mode === WorkflowLookupMode.Edit && existingRow) {
      const conn =
        customerConnections.find(
          (c) => c.ConnectionId === existingRow.CustomerConnectionId
        ) || null;
      setSelectedCustomerConnection(conn);
    } else if (mode === WorkflowLookupMode.Add) {
      setSelectedCustomerConnection(null);
      setSelectedWarehouse(null);
      setCustomerWarehouses([]);
      setCustomerWarehousesLoading(false);
      setAssociatedErps([]);
      setAssociatedErpsLoading(false);
      setSelectedErp(null);
    }
  }, [open]);

  const fetchAssociatedErps = async (selectedCustomerConnection, fetchAssociatedConnections) => {
    if (selectedCustomerConnection?.ConnectionId != null) {
      setAssociatedErpsLoading(true);
      try {
        const result = await fetchAssociatedConnections({
          connectionId: selectedCustomerConnection.ConnectionId,
        });
        setAssociatedErps(result);
      } catch {
        setAssociatedErps([]);
      } finally {
        setAssociatedErpsLoading(false);
      }
    } else {
      setAssociatedErps([]);
      setAssociatedErpsLoading(false);
    }
  }
  
  useEffect(() => {
    fetchAssociatedErps(
      selectedCustomerConnection,
      fetchAssociatedConnections
    );
  }, [selectedCustomerConnection, fetchAssociatedConnections]);
  // Fetch warehouses
  const fetchCustomerWarehouses = async (
    selectedErp,
    fetchCatalogArtifactData
  ) => {
    if (selectedErp?.ConnectionId != null) {
      setCustomerWarehousesLoading(true);
      try {
        const result = await fetchCatalogArtifactData({
          connectionId: selectedErp.ConnectionId,
          catalogType: CatalogTypes.Warehouse,
        });
        setCustomerWarehouses(result);
      } catch {
        setCustomerWarehouses([]);
      } finally {
        setCustomerWarehousesLoading(false);
      }
    } else {
      setCustomerWarehouses([]);
      setCustomerWarehousesLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomerWarehouses(
      selectedErp,
      fetchCatalogArtifactData
    );
  }, [selectedErp, fetchCatalogArtifactData]);

  // After warehouses load, pre-select value from existingRow in edit mode
  useEffect(() => {
    if (mode !== WorkflowLookupMode.Edit || !existingRow) return;
    if (!customerWarehouses?.length) return;

    const wh = customerWarehouses.find(
      (w) => w.Code === existingRow.ERPWarehouseCode
    );
    setSelectedWarehouse(wh || null);
  }, [mode, existingRow, customerWarehouses]);

  const validateForm = () => {
    if (!selectedCustomerConnection?.ConnectionId) {
      return {
        isValid: false,
        message: "Please select a Customer Connection before saving.",
      };
    }
    if (!selectedErp?.ConnectionId) {
      return {
        isValid: false,
        message: "Please select a Connection before saving.",
      };
    }
    if (!selectedWarehouse?.Code) {
      return {
        isValid: false,
        message: "Please select a Warehouse code before saving.",
      };
    }
    return { isValid: true };
  };

  const handleSubmit = async () => {
    const validation = validateForm();
    if (!validation.isValid) {
      dispatch(createAlert({ status: "Error", message: validation.message }));
      return;
    }

    const payload = {
      workflowLookupType,
      CustomerConnectionId: selectedCustomerConnection?.ConnectionId ?? null,
      CustomerConnectionName: selectedCustomerConnection?.Name ?? "",
      ERPWarehouseCode: selectedWarehouse?.Code ?? "",
      ERPConnectionName: selectedErp.ConnectionName ?? "",
    };

    const normalize = (v) =>
      v === null || v === undefined || v === 0 ? "" : String(v);

    // Find a matching row by normalized fields
    const matchingExistingRow = rows.find(
      (x) =>
        x.WorkflowLookupType == workflowLookupType &&
        normalize(x.CustomerConnectionId) ===
          normalize(selectedCustomerConnection?.ConnectionId) &&
        normalize(x.ERPWarehouseCode) === normalize(selectedWarehouse?.Code)
    );

    try {
      //Edit mode, update existing record directly
      if (mode === WorkflowLookupMode.Edit && existingRow?.WorkflowLookupId) {
        await axios.put(
          `/workflowlookups/${existingRow.WorkflowLookupId}`,
          payload
        );
      } else {
        if (matchingExistingRow && matchingExistingRow.WorkflowLookupId) {
          dispatch(
            createAlert({
              status: "Error",
              alertType: "result",
              title: "Unable to add customer warehouse",
              message: "Error. Customer warehouse already exists",
              buttonLabel: "Close",
              onButtonClick: () => {},
            })
          );
          return;
        } else {
          await axios.post("/workflowlookups", payload);
        }
      }

      if (fetchData) fetchData();
      handleClose();
    } catch (e) {
      dispatch(createAlert({ status: "Error", message: e.message }));
    }
  };

  const handleClose = () => {
    setSelectedCustomerConnection(null);
    setSelectedWarehouse(null);
    setCustomerWarehouses([]);
    setCustomerWarehousesLoading(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={"md"}
      fullWidth={true}
      PaperProps={{ style: { borderRadius: 15 } }}
    >
      <DialogTitle
        className="add-crossref-dialog-title"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "var(--primary-main)",
          color: "var(--default-light)",
          fontSize: "24px",
          borderRadius: "15px 15px 0 0",
          padding: "16px 24px",
        }}
      >
        <div style={{ fontWeight: "var(--font-semi-bold)" }}>
          {mode === WorkflowLookupMode.Edit
            ? "Edit Customer Warehouse"
            : "Add Customer Warehouse"}
        </div>
        <IconButton
          className="closeIconButton"
          style={{ color: "var(--default-light)" }}
          onClick={handleClose}
          size="large"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        className="dialogContent"
        style={{
          backgroundColor: "var(--disabled)",
          padding: "35px",
          minWidth: 400,
        }}
      >
        <Box sx={{ ml: 3 }}>
          {/* Customer Connection */}
          <Box display="flex" alignItems="center" mb={1}>
            <Typography variant="body1" sx={labelSx}>
              Customer Connection:
            </Typography>
            <Autocomplete
              options={customerConnections}
              loading={!customersLoaded}
              getOptionLabel={(opt) =>
                typeof opt === "string" ? opt : opt.Name
              }
              value={selectedCustomerConnection}
              onChange={(_, newValue) => {
                setSelectedCustomerConnection(newValue || null);
                setSelectedWarehouse(null);
              }}
              disabled={mode === WorkflowLookupMode.Edit}
              renderInput={(params) => (
                <TextField
                  sx={{ minWidth: 350, height: 45 }}
                  {...params}
                  autoComplete="off"
                />
              )}
            />
          </Box>
          {/* Erp names */}
          <Box display="flex" alignItems="center" mb={1}>
            <Typography variant="body1" sx={labelSx}>
              Erp name:
            </Typography>
            <Autocomplete
              options={associatedErps.filter(
                (opt) => opt.ConnectionId !== selectedErp?.ConnectionId
              )}
              getOptionLabel={(opt) => {
                return codeFirstLabel(opt.ConnectionId, opt.ConnectionName);
              }}
              value={selectedErp}
              onChange={(_, newValue) => {
                setSelectedErp(newValue || "");
              }}
              disabled={!selectedCustomerConnection}
              loading={associatedErpsLoading}
              loadingText="Loading..."
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{ minWidth: 350, height: 45 }}
                  autoComplete="off"
                />
              )}
            />
          </Box>
          {/* Warehouse code */}
          <Box display="flex" alignItems="center" mb={1}>
            <Typography variant="body1" sx={labelSx}>
              Warehouse code:
            </Typography>
            <Autocomplete
              options={customerWarehouses.filter(
                (opt) => opt.Code !== selectedWarehouse?.Code
              )}
              getOptionLabel={(opt) => {
                return codeFirstLabel(opt.Code, opt.Name);
              }}
              value={selectedWarehouse}
              onChange={(_, newValue) => {
                setSelectedWarehouse(newValue || "");
              }}
              disabled={!selectedErp}
              loading={customerWarehousesLoading}
              loadingText="Loading..."
              renderInput={(params) => (
                <TextField
                  {...params}
                  sx={{ minWidth: 350, height: 45 }}
                  autoComplete="off"
                />
              )}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions style={{ padding: "16px 24px" }}>
        <ShepButton variant="secondary" onClick={handleClose}>
          Cancel
        </ShepButton>
        <ShepButton onClick={handleSubmit}>
          {mode === WorkflowLookupMode.Edit ? "Save" : "Add"}
        </ShepButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddCustomerWarehousePopup;
